// RFC-1371 §6 "Role × action", as executable predicates.
//
// This is a TRANSCRIPTION of the table, one function per row, and it is the
// only place the console decides what a role may do. Components ask a named
// question ("canInviteMembers") instead of testing `role === 'owner'` inline,
// so a rule that changes in the RFC changes in exactly one file — and so the
// unit test can assert the whole grid against the doc rather than against
// whatever the UI happens to render.
//
// These are UI gates, not security. The server re-checks everything; §6's
// resolver is the real boundary. What they buy is the §6 requirement that a
// Member "must not see controls they cannot use" — an enabled button that
// 403s is a worse experience than an absent one, and it also leaks the shape
// of a team's administration to someone not entitled to it.
//
// Two rules in the table are about the TEAM, not the role, and are modelled as
// such below because forgetting them is how a personal team ends up with a
// Delete button (D10) that would leave the account with no owner scope at all.
//
// D10 is now NARROWER than the RFC's original text. A personal team is
// invitable, so it can hold a roster, and inviting, removing, capping and
// leaving all work there exactly as they do in a `kind='team'` team. Only two
// refusals survive, and both exist to keep the account's own scope intact: a
// personal team cannot be deleted, and its OWNER cannot leave it. Members the
// Owner invited may leave freely.

import type { TeamKind, TeamRole } from '../../api/teams'

/** The minimum a permission question needs: who you are, and where. */
export interface TeamContext {
  role: TeamRole
  kind: TeamKind
}

function isElevated(role: TeamRole): boolean {
  return role === 'owner' || role === 'admin'
}

// ── Projects ───────────────────────────────────────────────────────────────

/** Row: "Publish own private project to team" — ✓ for every role. */
export function canPublishOwnProject(): boolean {
  return true
}

/**
 * Row: "Delete team-visible project" — Owner and Admin ONLY.
 *
 * Not the creator. Once a project is published to the team, deleting it
 * destroys work the whole team can see and may be relying on, so it stops
 * being the creator's unilateral call. Publishing is one-way (D3) and this is
 * the other half of that bargain: you give the team visibility, and with it a
 * say in whether the thing survives.
 *
 * This deliberately differs from an earlier draft that allowed the creator.
 * Mirrors `require_project_permission(..., "admin")` on DELETE /projects/{id};
 * the server is the enforcement and this only decides whether to draw a button
 * that would 403.
 *
 * PRIVATE projects are not covered here at all -- they stay creator-only via
 * the resolver (rule 4), so deleting your own unpublished work is unchanged.
 */
export function canDeleteTeamProject(ctx: TeamContext): boolean {
  return isElevated(ctx.role)
}

/** Row: "Clone team project to personal" — ✓ for every role (D6). */
export function canCloneToPersonal(): boolean {
  return true
}

// ── Membership ─────────────────────────────────────────────────────────────

/** Row: "Invite / remove members" — Owner and Admin only. */
export function canManageMembers(ctx: TeamContext): boolean {
  return isElevated(ctx.role)
}

/**
 * Row: "Change roles" — Owner yes; Admin yes but NEVER to or from Owner.
 *
 * The from-Owner half matters as much as the to-Owner half: without it an
 * Admin could demote the Owner and then promote themselves, which is the same
 * escalation by two clicks instead of one.
 */
export function canChangeRole(ctx: TeamContext, fromRole: TeamRole, toRole: TeamRole): boolean {
  // `owner` is not assignable through a role change -- by ANYONE, including the
  // Owner. A team has exactly one Owner (`uq_team_members_owner` is a partial
  // unique index), so "promote Bob to Owner" could never add one; it replaced
  // one, silently demoting the caller to Admin. An Owner picking what read as a
  // promotion gave their team away, with no dialog and no undo.
  //
  // Mirrors `authorize_role_change` server-side. The server is the enforcement;
  // this exists so the UI does not offer an option that would 403.
  if (fromRole === 'owner' || toRole === 'owner') return false
  return ctx.role === 'owner' || ctx.role === 'admin'
}

/** Which roles this actor may pick for `member` in a role dropdown. */
export function assignableRoles(ctx: TeamContext, member: { role: TeamRole }): TeamRole[] {
  const all: TeamRole[] = ['owner', 'admin', 'member']
  return all.filter((next) => next !== member.role && canChangeRole(ctx, member.role, next))
}

/**
 * Row: "Remove members" — same gate as invite, with one addition the table
 * states elsewhere: you cannot remove the Owner. D7 makes Owner departure a
 * succession, which only the Owner can initiate by leaving.
 */
export function canRemoveMember(ctx: TeamContext, member: { role: TeamRole }): boolean {
  if (member.role === 'owner') return false
  return canManageMembers(ctx)
}

/**
 * Row: "Leave team" — Admin/Member ✓, Owner per D7.
 *
 * D10, narrowed: the OWNER of a personal team can never leave it, because
 * departure appoints a successor and that would hand the account's own scope to
 * someone the Owner invited, with no way to mint another (`uq_teams_personal`
 * is keyed on `created_by`). Anyone else in a personal team leaves normally —
 * being unable to walk out of a team you accepted an invitation to would be a
 * trap, not a safeguard. Mirrors `reject_personal_team_owner_departure`.
 *
 * The last-member block is a server fact and arrives via
 * `LeaveTeamPreview.canLeave`; this predicate answers only "should the control
 * exist", which is a different question from "will it succeed". Hiding it on
 * the last-member case would leave the user with no route to the explanation of
 * why they cannot leave.
 */
export function canLeaveTeam(ctx: TeamContext): boolean {
  if (ctx.kind === 'personal') return ctx.role !== 'owner'
  return true
}

// ── Team lifecycle ─────────────────────────────────────────────────────────

/** Row: "Manage team settings / limit" — Owner only; Admin explicitly ✗. */
export function canManageTeamSettings(ctx: TeamContext): boolean {
  return ctx.role === 'owner'
}

/** Row: "Delete team" — Owner only, and never a personal team (D10). */
export function canDeleteTeam(ctx: TeamContext): boolean {
  return ctx.role === 'owner' && ctx.kind !== 'personal'
}

// ── Credits (RFC-1372) ─────────────────────────────────────────────────────

/** Buy credits for the team: owner or admin only (card payments D1). */
export function canBuyCredits(ctx: TeamContext): boolean {
  return isElevated(ctx.role)
}

/**
 * C9: the credit history is visible to EVERY member.
 *
 * Present as a named predicate rather than omitted, so the decision is
 * discoverable at the call site. A pooled wallet whose funding members cannot
 * see is not reconcilable by the people spending from it, and the spend side is
 * already visible to them.
 */
export function canViewCreditHistory(): boolean {
  return true
}

/**
 * Row: "Set allowances" — Owner and Admin, in every team kind.
 *
 * Personal teams used to be excluded here, on the reasoning that one member who
 * is also the Owner can only ever cap themselves. They are invitable now, so
 * that reasoning is gone: a personal team's Owner budgets the people they
 * invited exactly as any other Owner does. `canSetAllowanceFor` still keeps an
 * Admin away from the Owner's own cap.
 */
export function canSetAllowances(ctx: TeamContext): boolean {
  return isElevated(ctx.role)
}

/**
 * Whether the actor may cap THIS member specifically.
 *
 * `canSetAllowances` answers "may this actor cap anyone here" and gates the
 * feature; this narrows it to one target, and the only narrowing is the Owner:
 * an Admin may not configure the Owner's quota, an Owner may configure an
 * Admin's.
 *
 * The asymmetry matches the rest of §6. Admins hold `manage_members` but not
 * `manage_settings` or `delete_team`, and `authorize_role_change` refuses to
 * let anyone set or clear `owner` — the Owner is a protected position, and a
 * cap an Admin can impose on it is a hole in that. It is not a privilege
 * escalation (the Owner can clear their own cap, since they hold
 * `manage_allowances` too), but an Admin should not be able to interrupt the
 * Owner's spending at all.
 *
 * An Admin capping another Admin, or themselves, is unchanged and allowed: an
 * allowance is a budgeting tool, and every elevated role can already spend the
 * pool.
 */
export function canSetAllowanceFor(ctx: TeamContext, targetRole: TeamRole): boolean {
  if (!canSetAllowances(ctx)) return false
  return !(ctx.role === 'admin' && targetRole === 'owner')
}
